import React, { useState, useCallback } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  LinearProgress,
  IconButton,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { CloudUpload, Close, ArrowBack } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useOnboardingAuth } from '../../hooks/useOnboardingAuth';
import { vendorImageService } from '../../services/vendorImageService';

// S3 asset URL
const logoImg = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/logo.png';

const OnboardingStep3: React.FC = () => {
  usePageTitle('Upload Image');
  
  const navigate = useNavigate();
  const { vendor, isLoading: authLoading } = useOnboardingAuth({ currentStep: 2 });
  
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (file) {
      setUploadedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp']
    },
    maxFiles: 1,
    multiple: false
  });

  const removeFile = () => {
    setUploadedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const handleContinue = async () => {
    if (!uploadedFile) {
      toast.error('Please select an image to upload');
      return;
    }

    if (!vendor) {
      toast.error('No vendor session found. Please try again.');
      return;
    }

    setIsSubmitting(true);
    setUploadProgress('Preparing upload...');

    try {
      // Step 1: Generate pre-signed upload URL
      setUploadProgress('Generating upload URL...');
      const urlData = await vendorImageService.generateUploadUrl({
        vendorId: vendor.vendorId,
        fileType: uploadedFile.type,
      });

      // Step 2: Upload directly to S3
      setUploadProgress('Uploading image to S3...');
      await vendorImageService.uploadToS3(urlData.uploadUrl, uploadedFile);

      // Step 3: Confirm upload with backend
      setUploadProgress('Confirming upload...');
      const confirmResponse = await vendorImageService.confirmUpload({
        vendorId: vendor.vendorId,
        imageUrl: urlData.imageUrl,
      });

      if (confirmResponse.success) {
        toast.success('Profile image uploaded successfully!');
        setUploadProgress('');
        // Navigate to next step after a brief delay
        setTimeout(() => {
          navigate('/onboarding/step4');
        }, 1000);
      } else {
        // Don't show error here - let the catch block handle it
        setIsSubmitting(false);
        setUploadProgress('');
      }
    } catch (error: any) {
      console.error('Error uploading image:', error);
      
      // Handle specific error messages
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message ||
                          'Failed to upload image. Please try again.';
      
      toast.error(errorMessage);
      setIsSubmitting(false);
      setUploadProgress('');
    }
  };

  const handleBack = () => {
    navigate('/onboarding/step2');
  };

  const progress = 60; // 3 of 5 steps = 60%

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#ffffff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 0',
          width: '100vw'
        }}
      >
        <CircularProgress size={60} sx={{ color: '#67314f', marginBottom: '20px' }} />
        <Typography
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 400,
            fontSize: '16px',
            color: '#181818',
            textAlign: 'center'
          }}
        >
          Loading...
        </Typography>
      </Box>
    );
  }

  // Show form only if vendor is authenticated
  if (!vendor) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '40px 0',
        width: '100vw'
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          width: '120px',
          height: '94px',
          marginBottom: '40px'
        }}
      >
        <Box
          component="img"
          src={logoImg}
          alt="Eventi Logo"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'contain'
          }}
        />
      </Box>

      {/* Main Content Container */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '40px',
          width: '100%',
          maxWidth: '540px',
          padding: '0 20px'
        }}
      >
        {/* Title Section */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 700,
              fontSize: '44px',
              lineHeight: 1.15,
              letterSpacing: '-0.44px',
              color: '#181818',
              marginBottom: '16px',
              textAlign: 'left'
            }}
          >
            Complete profile
          </Typography>
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: 1.4,
              color: '#181818',
              marginBottom: '16px',
              textAlign: 'left'
            }}
          >
            Complete your company Eventi profile to access your management dashboard. This will only take couple of minutes.
          </Typography>
          
          {/* Progress Indicator */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '16px' }}>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                color: '#181818'
              }}
            >
              3 of 5
            </Typography>
          </Box>
          
          {/* Progress Bar */}
          <Box sx={{ width: '100%', margin: '0 auto' }}>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: '4px',
                borderRadius: '2px',
                backgroundColor: '#f0f0f0',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#67314f',
                  borderRadius: '2px'
                }
              }}
            />
          </Box>
        </Box>

        {/* Form Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}
        >
          {/* Section Title */}
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 600,
              fontSize: '20px',
              lineHeight: 1.2,
              color: '#181818',
              marginBottom: '8px'
            }}
          >
            Profile Image
          </Typography>

          {/* File Upload Area */}
          <Box
            {...getRootProps()}
            sx={{
              width: '100%',
              height: '281px',
              border: '2px dashed #d0d0d0',
              borderRadius: '12px',
              backgroundColor: isDragActive ? '#f8f9fa' : '#fafafa',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#67314f',
                backgroundColor: '#f8f9fa'
              },
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <input {...getInputProps()} />
            
            {previewUrl ? (
              // Show preview when file is uploaded
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Upload preview"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    borderRadius: '8px'
                  }}
                />
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile();
                  }}
                  sx={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.7)'
                    }
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              // Show upload prompt when no file
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '16px',
                  textAlign: 'center'
                }}
              >
                <CloudUpload
                  sx={{
                    fontSize: '48px',
                    color: isDragActive ? '#67314f' : '#93989c'
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 600,
                      fontSize: '16px',
                      color: '#181818',
                      marginBottom: '8px'
                    }}
                  >
                    {isDragActive ? 'Drop your image here' : 'Upload your company logo'}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 400,
                      fontSize: '14px',
                      color: '#93989c'
                    }}
                  >
                    Drag and drop or click to browse
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"Inter", sans-serif',
                      fontWeight: 400,
                      fontSize: '12px',
                      color: '#93989c',
                      marginTop: '4px'
                    }}
                  >
                    PNG, JPG, GIF up to 10MB
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              marginTop: '16px'
            }}
          >
            {/* Continue Button */}
            <Button
              onClick={handleContinue}
              disabled={!uploadedFile || isSubmitting}
              fullWidth
              sx={{
                height: '56px',
                backgroundColor: '#67314f',
                borderRadius: '16px',
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: '16px',
                letterSpacing: '-0.32px',
                textTransform: 'none',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#5a2a42'
                },
                '&:disabled': {
                  backgroundColor: '#93989c',
                  color: 'white'
                }
              }}
            >
              {isSubmitting ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                  <Typography sx={{ fontSize: '14px' }}>{uploadProgress}</Typography>
                </Box>
              ) : (
                'Continue'
              )}
            </Button>

            {/* Back Button */}
            <Button
              onClick={handleBack}
              fullWidth
              startIcon={<ArrowBack />}
              sx={{
                height: '56px',
                backgroundColor: 'transparent',
                borderRadius: '16px',
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: '16px',
                letterSpacing: '-0.32px',
                textTransform: 'none',
                color: '#67314f',
                '&:hover': {
                  backgroundColor: 'rgba(103, 49, 79, 0.04)'
                },
                '& .MuiButton-startIcon': {
                  marginRight: '8px'
                }
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingStep3;
